import { FunctionComponent } from 'react';
import styled, { keyframes } from 'styled-components';
import logoAdobe from '../../images/home/logos/logo-adobe.png';
import logoAmazon from '../../images/home/logos/logo-amazon.png';
import logoUpwork from '../../images/home/logos/logo-upwork.png';
import logoYelp from '../../images/home/logos/logo-yelp.png';
import logoCreativeMornings from '../../images/home/logos/logo-creativemornings.png';
import logoZoom from '../../images/home/logos/logo-zoom.png';
import logoGoogle from '../../images/home/logos/logo-google.png';
import logoMiro from '../../images/home/logos/logo-miro.png';
import logoNike from '../../images/home/logos/logo-nike.webp';
import logoHubspot from '../../images/home/logos/logo-hubspot.webp';
import logoIkea from '../../images/home/logos/logo-ikea.webp';
import logoCultureAmp from '../../images/home/logos/logo-cultureamp.webp';
import logoTedx from '../../images/home/logos/logo-tedx.webp';
import logoUnitedNations from '../../images/home/logos/logo-united-nations.webp';
import logoWebflow from '../../images/home/logos/logo-webflow.webp';
import logoRolandBerger from '../../images/home/logos/logo-roland-berger.webp';
import logoShowpad from '../../images/home/logos/logo-showpad.webp';
import logoLeica from '../../images/home/logos/logo-leica-geosystems.webp';
import logoTuWien from '../../images/home/logos/logo-tu-wien.webp';
import logoGamificationEurope from '../../images/home/logos/logo-gamification-europe.webp';
import logoSuzy from '../../images/home/logos/logo-suzy.webp';

// The website's "Trusted by" strip (cozyjuicyreal/src/components/sections/TrustBar.tsx).
// Best-known names are spread through the loop so every stretch of it has one.
const LOGOS = [
  { src: logoGoogle, alt: 'Google' },
  { src: logoNike, alt: 'Nike' },
  { src: logoAdobe, alt: 'Adobe' },
  { src: logoHubspot, alt: 'HubSpot' },
  { src: logoIkea, alt: 'IKEA' },
  { src: logoAmazon, alt: 'Amazon' },
  { src: logoMiro, alt: 'Miro' },
  { src: logoZoom, alt: 'Zoom' },
  { src: logoCultureAmp, alt: 'Culture Amp' },
  { src: logoTedx, alt: 'TEDx' },
  { src: logoUpwork, alt: 'Upwork' },
  { src: logoUnitedNations, alt: 'United Nations' },
  { src: logoYelp, alt: 'Yelp' },
  { src: logoWebflow, alt: 'Webflow' },
  { src: logoRolandBerger, alt: 'Roland Berger' },
  { src: logoCreativeMornings, alt: 'CreativeMornings' },
  { src: logoShowpad, alt: 'Showpad' },
  { src: logoLeica, alt: 'Leica Geosystems' },
  { src: logoTuWien, alt: 'TU Wien' },
  { src: logoGamificationEurope, alt: 'Gamification Europe' },
  { src: logoSuzy, alt: 'Suzy' },
];

export const LogoMarquee: FunctionComponent = () => (
  <Strip role="region" aria-label="Trusted by">
    {/* Duplicated once so a -50% translate loops seamlessly; only the first set is announced. */}
    <Track>
      {[...LOGOS, ...LOGOS].map((logo, i) => (
        <LogoCell key={`${logo.alt}-${i}`} aria-hidden={i >= LOGOS.length ? 'true' : undefined}>
          <img
            src={logo.src}
            alt={i >= LOGOS.length ? '' : logo.alt}
            loading="lazy"
            decoding="async"
          />
        </LogoCell>
      ))}
    </Track>
  </Strip>
);

const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Strip = styled('div')`
  position: relative;
  overflow: hidden;

  /* Soft fade on both edges */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;
    width: 64px;
    pointer-events: none;

    @media (min-width: 768px) {
      width: 96px;
    }
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--background), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--background), transparent);
  }

  &:hover > div {
    animation-play-state: paused;
  }
`;

const Track = styled('div')`
  display: flex;
  width: max-content;
  /* About 6.5s per logo keeps the speed steady however many logos there are. */
  animation: ${marquee} ${LOGOS.length * 6.5}s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const LogoCell = styled('div')`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  padding: 0 24px;

  img {
    width: auto;
    max-width: 86%;
    height: 46px;
    object-fit: contain;
    filter: grayscale(1);
    opacity: 0.75;
    transition: filter 300ms, opacity 300ms;
  }

  img:hover {
    filter: none;
    opacity: 1;
  }

  @media (min-width: 640px) {
    min-width: 170px;

    img {
      height: 51px;
    }
  }

  @media (min-width: 768px) {
    min-width: 220px;
    padding: 0 32px;

    img {
      height: 64px;
    }
  }

  @media (min-width: 1024px) {
    min-width: 250px;
  }
`;
